import { MAX_SPICES, TexType, getTexConfig } from "@/data/schema";
import { useCallback, useEffect, useMemo , useState } from "react";
import { DataArrayTexture, RGBAFormat, UnsignedByteType, LinearMipmapLinearFilter, LinearFilter, SRGBColorSpace, NoColorSpace, RepeatWrapping} from "three"; //prettier-ignore
import { LoadingStatus } from "@/features/loading/stores/useLoadingStore";

export type Layer = {
  id: number;
  texSlot: number;
  index: number;
  data: null | Uint8Array;
  hasSourceData: boolean; // describes texture data
  // has compiled
  needsUpdate: boolean; 
  clearUpdate: () => void;
  //version: number;
};

export type Layers = Map<number, Layer>;

const VALID_SLOTS = new Set(Array.from({ length: MAX_SPICES }, (_, i) => i));
function getFreeSlot(layers: Layers): number | undefined {
  const usedSlots = new Set([...layers.values()].map((l) => l.texSlot));
  for (const slot of VALID_SLOTS) { if (!usedSlots.has(slot)) return slot; } //prettier-ignore
  return undefined;
}

/** Create DataArrayTexture - can be called at module level for preloading */
export function createDataArrayTexture(type: TexType): DataArrayTexture {
  const config = getTexConfig(type);
  const t = new DataArrayTexture(
    new Uint8Array(config.channels * config.size * config.depth),
    config.width,
    config.height,
    config.depth
  );
  t.format = RGBAFormat;
  t.type = UnsignedByteType;
  t.wrapS = RepeatWrapping;
  t.wrapT = RepeatWrapping;
  t.minFilter = LinearMipmapLinearFilter;
  t.magFilter = LinearFilter;
  t.colorSpace = type === TexType.NORMAL ? NoColorSpace : SRGBColorSpace;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}

export function useTextureManager(type: TexType) {
  const config = getTexConfig(type);
  const tex = useMemo(() => createDataArrayTexture(type), [type]);
  const [layers, setLayers] = useState<Layers>(() => new Map());
  const [status, setStatus] = useState<LoadingStatus>(LoadingStatus.IDLE)

  /** These are fine, can be changed up to ensure more secure and less error prone and error logging */
  const allocate = useCallback((id: number) => {
    setLayers((prev) => {
      if (prev.has(id)) return prev;

      const texSlot = getFreeSlot(prev);
      if (texSlot === undefined) {
        console.error("No free texture slots available");
        return prev;
      }

      const next = new Map(prev);
      next.set(id, {
        id,
        index: prev.size,
        texSlot,
        data: null,
        hasSourceData: false,
        needsUpdate: false,
      } as Layer);
      return next;
    });

  }, []);

  /** These are fine, can be changed up to ensure more secure and less error prone and error logging */
  const free = useCallback((id: number) => {
    setLayers((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  }, []);

  /** Sync logic can potentially be changed for the better */
  const sync = useCallback(
    (layers: Set<number>, targetIds: Set<number>) => {
      const toDelete = layers.difference(targetIds);
      const toCreate = targetIds.difference(layers);

      toDelete.forEach((id) => free(id));
      toCreate.forEach((id) => allocate(id));

      return { toCreate, toDelete };
    },
    [allocate, free]
  );

  /** Sync logic can potentially be changed for the better */
  const write = useCallback((id: number, imageData: Uint8Array) => {
    setLayers((prev) => {
      if (!prev.has(id)) return prev;
      const layer = prev.get(id);
      if (!layer) { throw new Error(`Layer with id ${id} not found`); } //prettier-ignore

      // state update
      const next = new Map(prev);
      next.set(id, {
        ...layer,
        data: imageData,
        hasSourceData: false,
        needsUpdate: true,
        clearUpdate: layer.clearUpdate, // preserve the method
      });
      return next;
    });
  }, []);

  /** Gpu updates */
  useEffect(() => {
    layers.forEach((layer) => {
      if (!layer.hasSourceData && layer.data) {
        
        const slot = layer.texSlot;
        const start = slot * config.size * config.channels;

        // write 
        (tex.image.data as Uint8Array).set(layer.data, start);

        // update the layer in state to reflect that GPU is synced
        setLayers((prev) => {
          const next = new Map(prev);
          const current = next.get(layer.id);
          if (!current) return next;
          next.set(layer.id, {
            ...current,
            hasSourceData: true,
            needsUpdate: true,
          });
          return next;
        });

        // Texture updates
        tex.addLayerUpdate(slot)
        tex.needsUpdate = true
      }
        // // @ts-expect-error typo in @types/three that calls it clearLayoutUpdates, but the real function is clearLayerUpdates
        // tex.clearLayerUpdates()      
    });

  }, [layers, tex, config]);

  /** Status - aggregate status for all layers of this texture type */
  useEffect(() => {
    const layerArray = [...layers.values()];

    if (layerArray.length === 0) 
    {
      setStatus(LoadingStatus.IDLE);
    }

    else if (layerArray.every((l) => l.hasSourceData)) 
    {
      setStatus(LoadingStatus.LOADED);
    }

    else 
    {
      setStatus(LoadingStatus.LOADING);
    }

  }, [layers]);

  const manager = useMemo( () => ({ allocate, free, write, sync }), [allocate, free, write, sync]); //prettier-ignore

  // Memoize return object to prevent unnecessary re-renders in consumers
  return useMemo(() => ({ tex, layers, status, manager }), [tex, layers, status, manager]);
}
